import {
  GraphQLID,
  GraphQLFieldConfig,
  GraphQLInputObjectType,
  GraphQLNonNull,
  GraphQLString,
  GraphQLList
} from 'graphql'
import {
  GenericMutationResponseType,
  GenericMutationResponse
} from '../types/generic-mutation-response'
import {
  vulnerabilityFilterArgType,
  VulnerabilityFilterArg
} from '../types/vulnerability-filter-arg'
import { Context, VulnerabilityStatus } from '../shared'
import {
  vulnerabilityStatusType,
  GQLVulnerability,
  vulnerabilityType
} from '../types/vulnerability'
import { mapVulnerabilityFilters } from '../endpoint-helpers/vulnerabilities'
import { findByIDAndOrganizationID } from '../queries/helpers/vulnerability'

interface VulnerabilityUpdateStatusArgs {
  readonly input?: {
    readonly organizationID: string
    readonly id: string
    readonly status: VulnerabilityStatus
    readonly substatus?: string
    readonly reasoning?: string
  }
}

const vulnerabilityUpdateStatusMutationArgs = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'VulnerabilityUpdateStatusMutationArgs',
      fields: () => ({
        organizationID: {
          description:
            'The ID of the organization for the vulnerability being updated',
          type: new GraphQLNonNull(GraphQLID)
        },
        id: {
          description: 'The id of the vulnerability being updated',
          type: new GraphQLNonNull(GraphQLID)
        },
        status: {
          description: 'The new status of the vulnerability',
          type: new GraphQLNonNull(vulnerabilityStatusType)
        },
        substatus: {
          description:
            'Optional secondary status when vulnerability is marked as Not A Problem',
          type: GraphQLString
        },
        reasoning: {
          description: 'Optional description of the status change',
          type: GraphQLString
        }
      })
    })
  }
}

export const updateVulnerabilityStatus: GraphQLFieldConfig<
  any,
  Context,
  VulnerabilityUpdateStatusArgs
> = {
  description: 'Update status of a single vulnerability',
  type: new GraphQLNonNull(vulnerabilityType),
  args: vulnerabilityUpdateStatusMutationArgs,
  resolve: async function resolve(
    _source,
    args,
    context
  ): Promise<GQLVulnerability> {
    const { input } = args

    if (!input) {
      throw new Error('mutation input parameter is required')
    }

    const { organizationID, id, status, substatus, reasoning } = input

    const { connector } = context

    await connector.put(`/${organizationID}/orgtraces/mark`, {
      traces: [id],
      status,
      substatus,
      note: reasoning
    })

    return findByIDAndOrganizationID(id, organizationID, connector)
  }
}

interface VulnerabilitiesUpdateStatusArgs {
  readonly input?: {
    readonly organizationID: string
    readonly ids: string[]
    readonly status: VulnerabilityStatus
    readonly substatus?: string
    readonly reasoning?: string
  }
}

const vulnerabilitiesUpdateStatusMutationArgs = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'VulnerabilitiesUpdateStatusMutationArgs',
      fields: () => ({
        organizationID: {
          description:
            'The ID of the organization for the vulnerabilities being updated',
          type: new GraphQLNonNull(GraphQLID)
        },
        ids: {
          description: 'A list of IDs of the vulnerabilities being updated',
          type: new GraphQLNonNull(
            new GraphQLList(new GraphQLNonNull(GraphQLID))
          )
        },
        status: {
          description: 'The new status of the vulnerabilities',
          type: new GraphQLNonNull(vulnerabilityStatusType)
        },
        substatus: {
          description:
            'Optional secondary status when vulnerabilities are marked as Not A Problem',
          type: GraphQLString
        },
        reasoning: {
          description: 'Optional description of the status change',
          type: GraphQLString
        }
      })
    })
  }
}

export const updateVulnerabilitiesStatus: GraphQLFieldConfig<
  any,
  Context,
  VulnerabilitiesUpdateStatusArgs
> = {
  description: 'Update status of a list of vulnerabilities',
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: vulnerabilitiesUpdateStatusMutationArgs,
  resolve: async function resolve(
    _source,
    args,
    context
  ): Promise<GenericMutationResponse> {
    const { input } = args

    if (!input) {
      throw new Error('mutation input parameter is required')
    }

    const { organizationID, ids, status, substatus, reasoning } = input

    const { connector } = context

    await connector.put(`/${organizationID}/orgtraces/mark`, {
      traces: ids,
      status,
      substatus,
      note: reasoning
    })

    return { _placeholder: true }
  }
}

interface VulnerabilitiesUpdateStatusByFiltersArgs {
  readonly input?: {
    readonly organizationID: string
    readonly filters: VulnerabilityFilterArg
    readonly excludedVulnerabilities: string[]
    readonly status: VulnerabilityStatus
    readonly substatus?: string
    readonly reasoning?: string
  }
}

const updateStatusByFiltersArg = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'VulnerabilitiesUpdateStatusByFiltersMutationArgs',
      fields: {
        organizationID: {
          description:
            'The ID of the organization for the vulnerabilities being updated',
          type: new GraphQLNonNull(GraphQLID)
        },
        filters: {
          type: new GraphQLNonNull(vulnerabilityFilterArgType)
        },
        excludedVulnerabilities: {
          description: 'A list of vulnerabilities to exclude from the filter',
          type: new GraphQLNonNull(
            new GraphQLList(new GraphQLNonNull(GraphQLID))
          )
        },
        status: {
          description: 'The new status of the vulnerabilities',
          type: new GraphQLNonNull(vulnerabilityStatusType)
        },
        substatus: {
          description:
            'Optional secondary status when vulnerabilities are marked as Not A Problem',
          type: GraphQLString
        },
        reasoning: {
          description: 'Optional description of the status change',
          type: GraphQLString
        }
      }
    })
  }
}

export const updateVulnerabilitiesStatusByFilters: GraphQLFieldConfig<
  undefined,
  Context,
  VulnerabilitiesUpdateStatusByFiltersArgs
> = {
  type: new GraphQLNonNull(GenericMutationResponseType),
  description: 'Update status of vulnerabilities using filters',
  args: updateStatusByFiltersArg,
  async resolve(_source, args, context): Promise<GenericMutationResponse> {
    if (!args.input) {
      throw new Error('mutation input parameter is required')
    }

    const {
      filters,
      organizationID,
      excludedVulnerabilities,
      status,
      substatus,
      reasoning: note
    } = args.input

    await context.connector.put(`${organizationID}/orgtraces/bulk/markStatus`, {
      filters: {
        ...mapVulnerabilityFilters(filters),
        excludedTraces: excludedVulnerabilities
      },
      status,
      substatus,
      note
    })

    return {
      _placeholder: true
    }
  }
}
